import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/spinner';
import { classifyError } from '@/lib/errors';
import { useEmailDetail } from '@/hooks/useTemplates';
import type { EmailQueueDetail } from '@/api/templates';

interface EmailDetailDialogProps {
  emailId: string | null;
  onOpenChange: (open: boolean) => void;
}

function statusBadgeVariant(status: string): 'default' | 'outline' | 'secondary' | 'destructive' {
  switch (status) {
    case 'SENT': return 'default';
    case 'FAILED': return 'destructive';
    case 'PROCESSING': return 'secondary';
    default: return 'outline';
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR');
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm break-words">{children}</div>
    </div>
  );
}

export function EmailDetailDialog({ emailId, onOpenChange }: EmailDetailDialogProps) {
  const { data, isLoading, error } = useEmailDetail(emailId);

  return (
    <Dialog open={!!emailId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes do email</DialogTitle>
          <DialogDescription>
            Metadados completos, último erro e variáveis de template.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <Spinner size={32} label="Carregando detalhes" />}

        {error && (
          <ErrorState title="Erro ao carregar detalhes" description={classifyError(error).message} />
        )}

        {data && <EmailDetailBody email={data} />}
      </DialogContent>
    </Dialog>
  );
}

function EmailDetailBody({ email }: { email: EmailQueueDetail }) {
  const templateVarsJson = email.template_vars
    ? JSON.stringify(email.template_vars, null, 2)
    : null;

  return (
    <div className="space-y-5">
      {/* Status + ID */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusBadgeVariant(email.status)}>{email.status}</Badge>
          <span className="text-sm text-muted-foreground">
            Tentativa {email.attempts}/{email.max_attempts}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-mono text-xs text-muted-foreground">{email.id}</span>
          <CopyButton text={email.id} label="Copiar ID" />
        </div>
      </div>

      {/* Erro — destaque quando houver */}
      {email.last_error && (
        <Alert variant="destructive">
          <AlertTitle>Último erro</AlertTitle>
          <AlertDescription>
            <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
              {email.last_error}
            </pre>
          </AlertDescription>
        </Alert>
      )}

      {/* Identificação do destinatário e finalidade */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Destinatário">
          <div>{email.to_email}</div>
          {email.to_name && <div className="text-muted-foreground">{email.to_name}</div>}
        </Field>
        <Field label="Finalidade">
          {email.finalidade_id ? (
            <code className="font-mono text-xs">{email.finalidade_id}</code>
          ) : '—'}
        </Field>
        <Field label="Assunto">
          <div className="break-all">{email.subject}</div>
        </Field>
        <Field label="Provider ID">
          {email.provider_id ? (
            <code className="font-mono text-xs">{email.provider_id}</code>
          ) : '—'}
        </Field>
      </section>

      {/* Linha do tempo */}
      <section>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
          Linha do tempo
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Criado:</span> {formatDate(email.created_at)}
          </div>
          <div>
            <span className="text-muted-foreground">Atualizado:</span> {formatDate(email.updated_at)}
          </div>
          <div>
            <span className="text-muted-foreground">Próx. tentativa:</span>{' '}
            {formatDate(email.next_attempt_at)}
          </div>
          <div>
            <span className="text-muted-foreground">Enviado em:</span> {formatDate(email.sent_at)}
          </div>
        </div>
      </section>

      {/* Vínculo com entidade */}
      {(email.entity_type || email.entity_id || email.usuario_id) && (
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {email.entity_type && (
            <Field label="Entidade">{email.entity_type}</Field>
          )}
          {email.entity_id && (
            <Field label="Entity ID">
              <code className="font-mono text-xs break-all">{email.entity_id}</code>
            </Field>
          )}
          {email.usuario_id && (
            <Field label="Usuário ID">
              <code className="font-mono text-xs break-all">{email.usuario_id}</code>
            </Field>
          )}
        </section>
      )}

      {/* Template vars */}
      {templateVarsJson && (
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
            Variáveis do template
          </h3>
          <pre className="rounded border bg-muted/50 p-3 font-mono text-xs leading-relaxed overflow-x-auto">
            {templateVarsJson}
          </pre>
        </section>
      )}

      {/* Body text — mostrado quando disponível, fallback para HTML cru */}
      {email.body_text && (
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
            Conteúdo (texto)
          </h3>
          <pre className="rounded border bg-muted/50 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words max-h-60 overflow-y-auto">
            {email.body_text}
          </pre>
        </section>
      )}
    </div>
  );
}
